import { useState } from "react";
import {
  formatPoolDenomination,
  type CatalogState,
  type ProtocolPool,
} from "@zkresistor/shared";
import {
  formatShieldedTvl,
  isLowLiquidityJetton,
  sortPools,
  type PoolSort,
} from "@zkresistor/client";
import { PoolAvatar } from "../components/Pool";
import { Loading } from "../components/Status";

interface CatalogPageProps {
  state: CatalogState;
  onSelect: (pool: ProtocolPool) => void;
}

export function CatalogPage({ state, onSelect }: CatalogPageProps) {
  const [kind, setKind] = useState<"ton" | "jetton">("ton");
  const [sort, setSort] = useState<PoolSort>("tvl");
  const [showLowLiquidity, setShowLowLiquidity] = useState(false);
  const gramCount = state.pools.filter((pool) => pool.kind === "ton").length;
  const jettonCount = state.pools.length - gramCount;
  const kindPools = state.pools.filter((pool) => pool.kind === kind);
  const lowLiquidityPools = kind === "jetton"
    ? kindPools.filter(isLowLiquidityJetton)
    : [];
  const regularPools = kindPools.filter((pool) => !isLowLiquidityJetton(pool));
  const pools = [
    ...sortPools(regularPools, sort),
    ...(showLowLiquidity ? sortPools(lowLiquidityPools, sort) : []),
  ];

  return (
    <section className="catalog">
      <div className="page-head">
        <h1>Pools</h1>
      </div>
      <div className="pool-controls-row">
        <div className="pool-kind-filter" role="tablist" aria-label="Pool type">
          <button
            type="button"
            role="tab"
            aria-selected={kind === "ton"}
            className={kind === "ton" ? "active" : ""}
            onClick={() => setKind("ton")}
          >
            GRAM <span>{gramCount}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={kind === "jetton"}
            className={kind === "jetton" ? "active" : ""}
            onClick={() => setKind("jetton")}
          >
            Jettons <span>{jettonCount}</span>
          </button>
        </div>
        <span className="pool-sort-control">
          <select
            className="pool-sort-select"
            aria-label="Sort Pools"
            value={sort}
            onChange={(event) => setSort(event.currentTarget.value as PoolSort)}
          >
            <option value="tvl">TVL locked</option>
            <option value="privacy">Privacy set</option>
          </select>
        </span>
      </div>

      {state.status === "error" && <p className="alert error">{state.error}</p>}
      {state.pools.length === 0 && state.status !== "error" && <Loading label="Reading signed catalog" />}
      {pools.length > 0 && (
        <div className="pool-list">
          {pools.map((pool) => (
            <button
              key={pool.id}
              type="button"
              className="pool-list-row"
              onClick={() => onSelect(pool)}
            >
              <PoolAvatar pool={pool} />
              <span className="pool-list-copy">
                <strong>{formatPoolDenomination(pool)} {pool.symbol}</strong>
                <small>{pool.kind === "ton" ? "GRAM Pool" : pool.name}</small>
              </span>
              <span className="pool-list-stat">
                <strong>{formatShieldedTvl(pool)} {pool.symbol} Shielded</strong>
                <small>{pool.nextIndex.toLocaleString("en-US")} deposit{pool.nextIndex === 1 ? "" : "s"}</small>
                <small>{pool.withdrawalCount.toLocaleString("en-US")} withdrawal{pool.withdrawalCount === 1 ? "" : "s"}</small>
              </span>
            </button>
          ))}
        </div>
      )}
      {kind === "jetton" && lowLiquidityPools.length > 0 && (
        <button
          type="button"
          className="low-liquidity-toggle"
          onClick={() => setShowLowLiquidity((shown) => !shown)}
        >
          {showLowLiquidity ? "Hide" : "Show"} low-liquidity Jettons ({lowLiquidityPools.length})
        </button>
      )}
      {state.pools.length > 0 && pools.length === 0 && lowLiquidityPools.length === 0 && (
        <p className="pool-list-empty">No {kind === "ton" ? "GRAM" : "Jetton"} pools</p>
      )}
    </section>
  );
}
